import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import ConsumerRegistrationForm from './ConsumerRegistrationForm';

/**
 * AdminConsumerRegistration
 * Drawer component for creating/editing consumer accounts
 * Wraps ConsumerRegistrationForm with drawer UI and state management
 */
const AdminConsumerRegistration = forwardRef(
  ({ isOpen, onClose, onSubmit, initialData, isLoading, title }, ref) => {
    const formRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentStep, setCurrentStep] = useState(initialData ? 2 : 1);

    // Sync current step from form
    useEffect(() => {
      const updateStep = () => {
        if (formRef.current?.getStep) {
          setCurrentStep(formRef.current.getStep());
        }
      };
      
      // Check immediately and then periodically
      updateStep();
      const interval = setInterval(updateStep, 100);
      return () => clearInterval(interval);
    }, [isOpen]);

    // Expose submitForm method to parent
    useImperativeHandle(ref, () => ({
      submitForm: async () => {
        if (formRef.current) {
          return formRef.current.submitForm();
        }
      },
      reset: (data) => {
        if (formRef.current) {
          formRef.current.reset(data);
          setCurrentStep(data ? 2 : 1);
        }
      },
    }));

    const handleFormSubmit = async (formData) => {
      try {
        setIsSubmitting(true);
        await onSubmit(formData);
      } finally {
        setIsSubmitting(false);
      }
    };

    if (!isOpen) return null;

    return (
      <>
        {/* Overlay */}
        <div
          className="fixed inset-0 z-40 bg-black/50 transition-opacity duration-300"
          onClick={onClose}
        />

        {/* Drawer */}
        <div
          className={`fixed right-0 top-0 h-full w-full max-w-[500px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          } z-50`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200">
            <div>
              <h2 className="text-[18px] font-bold text-slate-900">{title}</h2>
              <p className="text-[12px] text-slate-500 mt-1">
                {initialData ? 'Update consumer account details' : 'Create a new consumer account'}
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <ConsumerRegistrationForm
              ref={formRef}
              onSubmit={handleFormSubmit}
              initialData={initialData}
              isLoading={isSubmitting || isLoading}
            />
          </div>

          {/* Footer */}
          <div className="border-t border-slate-200 p-6 flex gap-3 bg-slate-50">
            <button
              onClick={onClose}
              disabled={isSubmitting || isLoading}
              className="px-4 py-2 text-[12px] font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            
            <div className="flex-1" />
            
            {/* Step 1: Next Button */}
            {currentStep === 1 && (
              <button
                onClick={() => {
                  if (formRef.current?.validate?.()) {
                    formRef.current?.setStep?.(2);
                    setCurrentStep(2);
                  }
                }}
                disabled={isSubmitting || isLoading}
                className="px-4 py-2 text-[12px] font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                Next: Profile
              </button>
            )}

            {/* Step 2: Back + Save Buttons */}
            {currentStep === 2 && (
              <>
                <button
                  onClick={() => {
                    formRef.current?.setStep?.(1);
                    setCurrentStep(1);
                  }}
                  disabled={isSubmitting || isLoading}
                  className="px-4 py-2 text-[12px] font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  onClick={() => formRef.current?.submitForm()}
                  disabled={isSubmitting || isLoading}
                  className="px-4 py-2 text-[12px] font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting || isLoading ? (
                    <>
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Consumer'
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </>
    );
  }
);

AdminConsumerRegistration.displayName = 'AdminConsumerRegistration';

export default AdminConsumerRegistration;
